<template>
  <v-dialog persistent v-model="dialog" max-width="900px">
    <template v-slot:activator="{ on, attrs }">
      <v-btn v-bind="attrs" v-on="on" @click="getCostSuggestions" color="#2f7a28" icon>
        <v-icon>mdi-account-cash</v-icon>
      </v-btn>
    </template>

    <v-card>
      <v-card-title class="d-flex justify-space-between sticky-line header-top">
        <span>Cost suggestions</span>
        <v-btn @click="clearAllData()" icon>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pb-1">
        <div v-if="isLoading" class="d-flex justify-center">
          <v-progress-circular indeterminate color="primary" :size="60" />
        </div>

        <div v-else>
          <div v-for="member in teamMemberIds" :key="member" class="mt-5">
            <v-row no-gutters>
              
              <v-col cols="3" class="pr-3 black--text">
                <div class="option-title item-title-font font-weight-bold mb-1">
                  {{ getFirstTitle(member) }}
                </div>
                <div class="option-allocation item-description-font mb-3">
                  {{ allocations[member] }}%
                </div>
              </v-col>

              <v-col cols="9">
                <v-radio-group v-model="selectedSuggestions[member]">
                  <div
                    v-for="(items, description) in groupedSuggestionsMap[member]"
                    :key="description"
                  >
                    <v-radio :value="description" class="d-flex align-start mb-2 custom-radio">
                      <template #label>
                        <div class="radio-label-row d-flex justify-space-between align-center flex-grow-1">

                          <div class="d-flex align-center">
                            <div>
                              <div class="font-weight-medium ml-1" style="min-width: 200px;">
                                {{ description }}
                              </div>
                              <div class="d-flex align-center mt-4 mb-3">
                                <v-icon
                                  v-for="(item, index) in items"
                                  :key="index"
                                  class="mx-2"
                                  @mouseover="setHovered(member, description, index)"
                                  @mouseleave="clearHovered(member, description)"
                                  @click.stop="selectLevel(member, description, index)"
                                  :class="{ 'star-selected': selectedLevels[member] && selectedLevels[member][description] === index }"
                                  style="cursor: pointer; color: #FF9800"
                                >
                                  {{
                                    (hovered[member] && hovered[member][description] >= index) ||
                                    (selectedLevels[member] && selectedLevels[member][description] >= index)
                                      ? 'mdi-star'
                                      : 'mdi-star-outline'
                                  }}
                                </v-icon>
                              </div>
                            </div>
                          </div>

                          <div
                            v-if="getActiveItem(member, description)"
                            class="d-flex flex-column text-right"
                            :style="{ color: getActiveItem(member, description).priceHintHexColor }"
                          >
                            <span class="black--text">
                              {{ getActiveItem(member, description).hourlyPrice }}$/hr |
                              {{ getActiveItem(member, description).monthlyPrice }}$/month
                            </span>
                            <span class="mt-1">
                              {{ getActiveItem(member, description).priceHint }}
                            </span>
                          </div>
                        </div>
                      </template>
                    </v-radio>
                  </div>
                </v-radio-group>
                <v-divider class="my-3" />
              </v-col>
            </v-row>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="sticky-line footer-bottom">
        <v-spacer />
        <v-btn color="primary" @click="setCostSuggestions">Save</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axiosInstance from "@/axios";

export default {
  props: {
    id: Number,
    refreshData: Function,
  },
  data: () => ({
    dialog: false,
    isLoading: false,
    groupedSuggestionsMap: {},
    allocations: {},
    teamMemberIds: [],
    selectedSuggestions: {},
    selectedLevels: {},
    hovered: {},
  }),
  computed: {
    teamMembers() {
      return this.$store.state.teamMembers || [];
    },
  },
  methods: {
    async getCostSuggestions() {
      try {
        this.isLoading = true;
        const { data } = await axiosInstance.get(
          `/proposal/${this.id}/TeamMember/auto/rates`
        );

        if (data && data.data) {
          this.groupedSuggestionsMap = {};
          this.allocations = {};
          this.teamMemberIds = [];

          data.data.forEach((member) => {
            const id = member.id;
            this.allocations[id] = member.allocation;
            this.teamMemberIds.push(id);

            const groups = member.suggestions.reduce((acc, s) => {
              if (!acc[s.description]) acc[s.description] = [];
              acc[s.description].push(s);
              return acc;
            }, {});

            this.groupedSuggestionsMap[id] = groups;

            const descriptions = Object.keys(groups);

            this.$set(this.selectedLevels, id, {});
            descriptions.forEach((desc) => {
              this.selectedLevels[id][desc] = 1;
            });

            if (descriptions.length > 0) {
              this.$set(this.selectedSuggestions, id, descriptions[0]);
            }
          });
        }
      } catch (err) {
        console.error(err);
        this.clearAllData();
      } finally {
        this.isLoading = false;
      }
    },

    getFirstTitle(teamMemberId) {
      const group = this.groupedSuggestionsMap[teamMemberId];
      const firstGroup = group && Object.values(group)[0];
      return firstGroup && firstGroup[0] && firstGroup[0].title || "Team Member";
    },

    selectLevel(memberId, description, index) {
      if (!this.selectedLevels[memberId]) {
        this.$set(this.selectedLevels, memberId, {});
      }
      this.$set(this.selectedLevels[memberId], description, index);
    },

    setHovered(memberId, description, index) {
      if (!this.hovered[memberId]) {
        this.$set(this.hovered, memberId, {});
      }
      this.$set(this.hovered[memberId], description, index);
    },

    clearHovered(memberId, description) {
      if (this.hovered[memberId]) {
        this.$delete(this.hovered[memberId], description);
      }
    },

    getActiveItem(memberId, description) {
      const items = this.groupedSuggestionsMap && this.groupedSuggestionsMap[memberId] && this.groupedSuggestionsMap[memberId][description];
      const hoveredIndex = this.hovered && this.hovered[memberId] && this.hovered[memberId][description];
      const selected = this.selectedLevels && this.selectedLevels[memberId] && this.selectedLevels[memberId][description];

      if (hoveredIndex != null) {
        return items && items[hoveredIndex];
      }
      if (selected != null) {
        return items && items[selected];
      }
      return null;
    },

    async setCostSuggestions() {
      try {
        this.isLoading = true;

        for (const teamMemberId of this.teamMemberIds) {
          const selectedDescription = this.selectedSuggestions[teamMemberId];
          if (!selectedDescription) continue;

          const suggestionGroup = this.groupedSuggestionsMap[teamMemberId][selectedDescription];
          const selectedIndex = this.selectedLevels && this.selectedLevels[teamMemberId] && this.selectedLevels[teamMemberId][selectedDescription];
          const selectedItem = (suggestionGroup && suggestionGroup[selectedIndex != null ? selectedIndex : 1]) || (suggestionGroup && suggestionGroup[0]);

          if (selectedItem) {
            const currentTeamMember = this.teamMembers.find(
              (tm) => tm.id === teamMemberId
            );

            const updated = {
              ...currentTeamMember,
              hourlyPayment: selectedItem.hourlyPrice,
              monthlyPayment: selectedItem.monthlyPrice,
            };

            await axiosInstance.put(
              `/proposal/${this.id}/TeamMember/${teamMemberId}`,
              updated
            );
          }
        }

        this.$store.dispatch("getSummaryData", this.$route.params.id);
        this.refreshData();
        this.dialog = false;
      } catch (e) {
        console.error(e);
      } finally {
        this.clearAllData();
      }
    },

    clearAllData() {
      this.dialog = false;
      this.isLoading = false;
      this.groupedSuggestionsMap = {};
      this.selectedSuggestions = {};
      this.selectedLevels = {};
      this.hovered = {};
      this.allocations = {};
      this.teamMemberIds = [];
    },
  },
};
</script>

<style scoped>
.sticky-line {
  position: sticky;
  background-color: white;
  z-index: 1;
}
.header-top {
  top: 0;
}
.footer-bottom {
  bottom: 0;
}
.star-selected {
  transform: scale(1.2);
}
.radio-label-row {
  width: 100%;
}
</style>
